// Global Instructions Rule Applied!
//
// Notice-period warning on the capital call form. The fund's LPA sets a
// minimum notice for an investor's initial contribution and another for
// later drawdowns (Fund, Terms tab, Capital Call Terms); when the call's due
// date gives less, this says so, per kind and with the number of investors
// affected. A warning, not a block: the general partner may have agreed a
// shorter period with the investors.
//
// Reads the same cc__notice_data the notices are built from, so the check
// uses the SAVED dates — the form hides it while the header has unsaved
// edits and re-reads it after every save / compute / issue (`version`).

import React, { useEffect, useState } from 'react'
import { Alert } from 'antd'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchCapitalCallNoticeData } from '../services/capitalCallsService.ts'
import type { CapitalCallNoticeData } from '../../../state_manager/data_models/capital_call_notice_data.ts'

interface NoticePeriodAlertProps {
  capitalCallId: string | null | undefined
  /** Changes whenever the form re-hydrates from the server. */
  version: unknown
  className?: string
}

/** The sentence per kind of call ("initial contribution" / "drawdown") whose
 *  required notice the due date misses. Exported for the test. */
export function noticePeriodShortfalls(data: CapitalCallNoticeData): string[] {
  const given = data.call.prog__notice_days_given
  if (given == null) return []
  const unit = data.call.prog__notice_days_business ? 'business day' : 'day'
  const kinds: Array<{ initial: boolean; label: string }> = [
    { initial: true, label: 'an initial contribution' },
    { initial: false, label: 'a drawdown' }
  ]
  return kinds.flatMap(({ initial, label }) => {
    const short = data.investors.filter((i) => i.prog__is_initial === initial && !i.prog__notice_period_ok)
    if (short.length === 0) return []
    const required = short[0].prog__notice_days_required
    return [
      `The fund's LPA requires ${required} ${unit}s' notice for ${label}; the due date gives ${given} ` +
        `(${short.length} investor${short.length === 1 ? '' : 's'}).`
    ]
  })
}

export default function NoticePeriodAlert({ capitalCallId, version, className }: NoticePeriodAlertProps) {
  const [shortfalls, setShortfalls] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    if (!capitalCallId) {
      setShortfalls([])
      return
    }
    const token = getAccessToken(store.getState().auth)
    fetchCapitalCallNoticeData(capitalCallId, token).then((res) => {
      if (cancelled) return
      // A failed read shows nothing: this is an advisory, and the notices
      // themselves report what stops them being produced.
      setShortfalls(res.data && res.data.call.prog__notice_applicable ? noticePeriodShortfalls(res.data) : [])
    })
    return () => {
      cancelled = true
    }
  }, [capitalCallId, version])

  if (shortfalls.length === 0) return null
  return (
    <Alert
      type='warning'
      showIcon
      className={className}
      message='The due date is inside the notice period'
      description={shortfalls.map((s) => (
        <div key={s}>{s}</div>
      ))}
    />
  )
}
